import React, { useMemo } from 'react';
import { Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { HotelPhotoCarousel } from '@shared/components/HotelPhotoCarousel';
import type { AccommodationListingDTO } from '@shared/types/Accommodations.types';
import { Skeleton } from '@shared/components/Skeleton';
import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';
import { AddedTag } from './AddedTag';

type ListingPhotosProps = Readonly<{
  listing: AccommodationListingDTO;
  isMobileOrTablet?: boolean;
  showAddedTag: boolean;
}>;

const ListingPhotos = ({ listing, isMobileOrTablet, showAddedTag }: ListingPhotosProps) => {
  const images = useMemo(() => {
    if (!listing.mediaAssets?.photos.length) {
      return [{ src: DEFAULT_HOTEL_IMAGE, alt: listing.name }];
    }

    return (
      listing.mediaAssets?.photos.map(x => ({
        src: x.url,
        alt: listing.name,
      })) || []
    );
  }, [listing.mediaAssets?.photos, listing.name]);

  if (isMobileOrTablet) {
    return (
      <Flex
        alignItems="center"
        justifyContent="center"
        aspectRatio="16/9"
        maxWidth="100%"
        minWidth="100%"
        position="relative"
      >
        {showAddedTag && <AddedTag listing={listing} />}
        <HotelPhotoCarousel
          hotel={listing}
          images={images}
          context="Accommodations.ListingPhotos"
          aspectRatio="16/9"
          imgProps={{
            borderRadius: pxToRem(10),
          }}
          navigation={{
            variant: 'minimal',
            visible: 'default',
          }}
        />
      </Flex>
    );
  }

  // Desktop: no aspectRatio — alignSelf="stretch" fills the parent row height.
  const leftOnlyRadius = `${pxToRem(10)} 0 0 ${pxToRem(10)}`;
  return (
    <HotelPhotoCarousel
      hotel={listing}
      images={images}
      context="Accommodations.ListingPhotos"
      width={pxToRem(278)}
      flexShrink={0}
      alignSelf="stretch"
      borderRadius={leftOnlyRadius}
      imgProps={{
        borderRadius: leftOnlyRadius,
      }}
      navigation={{
        variant: 'minimal',
        visible: 'default',
      }}
    >
      {showAddedTag && <AddedTag listing={listing} />}
    </HotelPhotoCarousel>
  );
};

ListingPhotos.Skeleton = ({ isMobileOrTablet = true }: { isMobileOrTablet?: boolean }) => {
  const leftOnlyRadius = `${pxToRem(10)} 0 0 ${pxToRem(10)}`;
  if (!isMobileOrTablet) {
    return (
      <Skeleton
        width={pxToRem(278)}
        flexShrink={0}
        alignSelf="stretch"
        borderRadius={leftOnlyRadius}
      />
    );
  }
  return (
    <Skeleton
      width="100%"
      aspectRatio="16/9"
      borderRadius={pxToRem(10)}
    />
  );
};

export { ListingPhotos };
